export type PortfolioShot = {
  src: string
  alt: string
  caption: string
  /** Phone captures use a tall frame; default is wide 16:10. */
  frame?: "wide" | "phone"
}

export type PortfolioProject = {
  id: string
  anchor: string
  title: string
  eyebrow: string
  description: string
  capabilities: string[]
  tech: string[]
  href?: string
  hrefLabel?: string
  /** Optional public YouTube embed (video id). Never use for FLOW. */
  youtubeVideoId?: string
  youtubeTitle?: string
  /** Local hosted demo clip (preferred over YouTube when set). */
  demoVideo?: {
    src: string
    srcWebm?: string
    poster?: string
    title: string
    caption?: string
  }
  primary: PortfolioShot
  gallery: PortfolioShot[]
}

/**
 * Selected Work.
 * 1) GameDNA  2) BVR site  3) FLOW  4) VOLT  5) AI Mastering
 */
export const PORTFOLIO_PROJECTS: PortfolioProject[] = [
  {
    id: "gamedna",
    anchor: "gamedna",
    title: "GAMEDNA",
    eyebrow: "SPORTS · EXPO APP",
    description:
      "Multi-sport consumer app for fans. Home feed, model projections, my picks, and odds desks with honest DEMO labels until Site feeds settle. No invented LIVE clocks.",
    capabilities: [
      "Onboarding into a multi-sport sports desk",
      "Home feed and predictions with credit-aware model cards",
      "My Picks board with hit rate and DEMO settlement labels",
      "NBA odds: spreads, totals, and moneylines when Site has rows",
    ],
    tech: ["Expo", "React Native", "TypeScript", "TanStack Query", "Site sports API"],
    href: "https://gamedna.us",
    hrefLabel: "OPEN GAMEDNA",
    primary: {
      src: "/images/portfolio/gamedna/app-home.webp",
      alt: "GameDNA Expo home with multi-sport latest news cards",
      caption: "Home feed",
      frame: "phone",
    },
    gallery: [
      {
        src: "/images/portfolio/gamedna/app-onboarding.webp",
        alt: "GameDNA Expo onboarding with sports desk feature cards",
        caption: "Onboarding",
        frame: "phone",
      },
      {
        src: "/images/portfolio/gamedna/app-predictions.webp",
        alt: "GameDNA Expo predictions with model projection and top picks",
        caption: "Predictions",
        frame: "phone",
      },
      {
        src: "/images/portfolio/gamedna/app-my-picks.webp",
        alt: "GameDNA Expo My Picks board with active forecasts and hit rate",
        caption: "My Picks",
        frame: "phone",
      },
      {
        src: "/images/portfolio/gamedna/app-odds.webp",
        alt: "GameDNA Expo NBA odds with spreads totals and moneylines",
        caption: "NBA Odds",
        frame: "phone",
      },
    ],
  },
  {
    id: "black-velvet-records",
    anchor: "black-velvet-records",
    title: "BLACK VELVET RECORDS",
    eyebrow: "THE MUSIC PLATFORM",
    description:
      "The BVR site itself. Artists, listeners, Song Wars, live hubs and discovery. The home for the music and the tools that ship around it.",
    capabilities: [
      "Artist directory and catalog",
      "Song Wars competitions",
      "Live host hubs and fan queues",
      "Music submission and discovery",
    ],
    tech: ["React", "Vite", "MUI", "Prisma", "Stripe"],
    href: "https://blackvelvetrecords.com",
    hrefLabel: "OPEN BLACK VELVET",
    primary: {
      src: "/images/portfolio/black-velvet-records/bvr-home.webp",
      alt: "Black Velvet Records homepage with dark cinematic hero",
      caption: "Home / discovery",
    },
    gallery: [
      {
        src: "/images/portfolio/black-velvet-records/song-war.webp",
        alt: "Song Wars competition page with completed bracket card",
        caption: "Song Wars",
      },
      {
        src: "/images/portfolio/black-velvet-records/artist-dashboard.webp",
        alt: "Black Velvet Records artists directory",
        caption: "Artists",
      },
      {
        src: "/images/portfolio/black-velvet-records/bvr-live.webp",
        alt: "Black Velvet Records live discovery",
        caption: "Live",
      },
    ],
  },
  {
    id: "bvr-flow",
    anchor: "bvr-flow",
    title: "BVR FLOW",
    eyebrow: "MUSIC SOFTWARE · ABLETON",
    description:
      "Ableton-connected production desk. FLOW arranges your samples into songs, places them into Live, and keeps Song Maker, Play and studio workflows in one place.",
    capabilities: [
      "Arranges your samples into song plans",
      "AI sample search and Ableton placement",
      "Song Maker builds tracks before touching Live",
      "Live bridge for Play and session control",
    ],
    tech: ["TypeScript", "Ableton bridge", "React", "sample library AI"],
    href: "https://blackvelvetrecords.com/store/software/bvr-flow",
    hrefLabel: "OPEN BVR FLOW",
    demoVideo: {
      src: "/videos/portfolio/bvr/flow-widgets.mp4",
      poster: "/images/portfolio/bvr/bvr-flow-song-maker.webp",
      title: "BVR FLOW Song Creator desk",
      caption: "Song Creator desk",
    },
    primary: {
      src: "/images/portfolio/bvr/bvr-flow-song-maker.webp",
      alt: "BVR FLOW Song Maker editor for arranging samples into a song",
      caption: "Song Maker editor",
    },
    gallery: [
      {
        src: "/images/portfolio/bvr/bvr-flow-widgets.webp",
        alt: "BVR FLOW Custom widgets page with live visualizers",
        caption: "Widgets desk",
      },
      {
        src: "/images/portfolio/bvr/bvr-flow-arrange.webp",
        alt: "BVR FLOW arranging sample tracks into Ableton with master waveform",
        caption: "Arranges samples into Live",
      },
      {
        src: "/images/portfolio/bvr/bvr-flow-samples.webp",
        alt: "BVR FLOW sample library with local packs and AI picks",
        caption: "Sample library",
      },
      {
        src: "/images/portfolio/bvr/bvr-flow-ableton.webp",
        alt: "BVR FLOW play screen with Ableton integration",
        caption: "Place into Ableton",
      },
    ],
  },
  {
    id: "bvr-volt",
    anchor: "bvr-volt",
    title: "BVR VOLT",
    eyebrow: "MUSIC SOFTWARE · SYNTH",
    description:
      "Subtractive synthesizer with AI patching, visualizer, PLAY keys and a host-sync step sequencer for grooves and performance.",
    capabilities: [
      "Subtractive synth engine",
      "AI patch design",
      "Step sequencer and patterns",
      "Visualizer and keyboard play modes",
    ],
    tech: ["JUCE", "C++", "TypeScript", "AI patch tools"],
    href: "https://blackvelvetrecords.com/store/software/bvr-volt",
    hrefLabel: "OPEN BVR VOLT",
    youtubeVideoId: "NwY8L8wduc8",
    youtubeTitle: "BVR VOLT public demo",
    primary: {
      src: "/images/portfolio/bvr/bvr-volt.webp",
      alt: "BVR VOLT synthesizer main interface",
      caption: "VOLT main",
    },
    gallery: [
      {
        src: "/images/portfolio/bvr/bvr-volt-sequencer.webp",
        alt: "BVR VOLT step sequencer grid in use",
        caption: "Sequencer",
      },
      {
        src: "/images/portfolio/bvr/bvr-volt-sequencer-ui.webp",
        alt: "BVR VOLT sequencer redesign UI",
        caption: "Sequencer lanes",
      },
      {
        src: "/images/portfolio/bvr/bvr-volt-visualizer.webp",
        alt: "BVR VOLT instrument controls and visualizer",
        caption: "Visualizer",
      },
      {
        src: "/images/portfolio/bvr/bvr-volt-keys.webp",
        alt: "BVR VOLT keyboard and sound design",
        caption: "PLAY keys",
      },
    ],
  },
  {
    id: "ai-mastering",
    anchor: "ai-mastering",
    title: "AI MASTERING",
    eyebrow: "OWN DSP · BVR ENGINE",
    description:
      "Automated mastering on BVR's own DSP stack. Analyze loudness and tone, plan EQ and dynamics with local AI, then render through a deterministic LocalDspEngine. No rented mastering black box as the default path.",
    capabilities: [
      "LocalDspEngine EQ, dynamics, stereo and limiting",
      "Audio profile analysis (LUFS, true peak, LRA)",
      "AI tonal plan validated before DSP runs",
      "Verification pass after each master",
    ],
    tech: ["TypeScript jobs", "Python worker", "FFmpeg", "pyloudnorm", "Ollama planner"],
    href: "https://blackvelvetrecords.com/store/software/mastering",
    hrefLabel: "OPEN AI MASTERING",
    primary: {
      src: "/images/portfolio/ai-mastering/dashboard.webp",
      alt: "AI Mix and Master dashboard with analysis and DSP stages",
      caption: "Mastering dashboard",
    },
    gallery: [
      {
        src: "/images/portfolio/ai-mastering/bvr-mastering.webp",
        alt: "BVR mastering product visual",
        caption: "BVR mastering",
      },
      {
        src: "/images/portfolio/ai-mastering/store-page.webp",
        alt: "BVR mastering store page",
        caption: "Store surface",
      },
    ],
  },
]
